import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useAlertsWebSocket from '../hooks/useAlertsWebSocket';
import useBreakpoint from '../hooks/useBreakpoint';

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  // #704 PR-6 — desktop nav consolidation: the 4 specialty screens
  // (Big Cheap Acres / Stale / Mineral / Retreat) collapse into a
  // single "Screens" dropdown so the desktop bar drops from 10 items
  // to 7 (Search, Top Picks, Properties, Screens, Trends, Saved,
  // Alerts) and the user widget. Phone + tablet behavior unchanged.
  const [screensOpen, setScreensOpen] = useState(false);
  const screensRef = useRef(null);
  const { user, logout } = useAuth();
  // Phone/tablet breakpoint: collapse the navbar to a logo-only
  // header. BottomTabBar/AppSidebar + MoreSheet own the primary nav
  // surfaces below the desktop breakpoint so the top bar doesn't
  // duplicate or crowd the nav links.
  const bp = useBreakpoint();
  const isCompact = bp !== 'desktop';

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setScreensOpen(false);
  }, [location.pathname]);

  // PR-6: close the Screens dropdown when clicking outside it.
  useEffect(() => {
    if (!screensOpen) return undefined;
    const onDocClick = (e) => {
      if (screensRef.current && !screensRef.current.contains(e.target)) {
        setScreensOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [screensOpen]);

  // Live unread-alert count. This used to be a 60s `setInterval` poll;
  // it's now a WebSocket push (the matcher broadcasts a fresh count
  // when it creates alerts) that transparently falls back to the same
  // 60s poll if the socket can't be established. Disabled on the
  // auth pages, where the badge isn't user-visible.
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const unreadCount = useAlertsWebSocket(!isAuthPage, user?.id ?? null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // PR-6 (#704): the 4 specialty screens collapse into a single
  // "Screens" dropdown. Order matches the blueprint table — Big Cheap
  // Acres first as the highest-traffic discovery screen, Buildable
  // Retreat last as the newest.
  const screenLinks = [
    { path: '/screens/big-cheap-acreage', label: 'Big Cheap Acres' },
    { path: '/screens/stale-acreage', label: 'Stale' },
    { path: '/screens/mineral-adjacent', label: 'Mineral' },
    { path: '/screens/buildable-retreat', label: 'Retreat' },
  ];
  const isOnScreens = location.pathname.startsWith('/screens/');

  // The /admin page calls endpoints that require is_admin=true, so
  // showing the nav link to non-admins just sets them up for a 403.
  // Hide it unless the authenticated user has the admin flag.
  // PR-6: top-level items drop from 10 to 7 (Search, Top Picks,
  // Properties, Screens-dropdown, Trends, Saved, Alerts) + optional
  // Admin. The Screens entry is rendered separately below because it
  // needs a controlled dropdown rather than a plain link.
  const navLinks = [
    { path: '/', label: 'Search' },
    { path: '/opportunities', label: 'Top Picks' },
    { path: '/properties', label: 'Properties' },
    { type: 'screens-dropdown' },
    { path: '/dashboard', label: 'Trends' },
    { path: '/saved', label: 'Saved' },
    { path: '/research-library', label: 'Research' },
    { path: '/watchlists', label: 'Alerts', badge: unreadCount },
    ...(user?.is_admin ? [{ path: '/admin', label: 'Admin' }] : []),
  ];

  // Compact: render a slim logo-only header. BottomTabBar/AppSidebar
  // handle navigation, MoreSheet handles secondary destinations + logout.
  // We intentionally keep the same `.navbar` shell so global styles
  // (sticky position, background, z-index) still apply.
  if (isCompact) {
    return (
      <nav className="navbar navbar--phone" data-testid="navbar-phone">
        <div className="navbar-content">
          <Link to="/" className="navbar-logo">
            LandAI
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar">
      <div className="navbar-content">
        <Link to="/" className="navbar-logo">
          LandAI
        </Link>
        <button
          className={`navbar-hamburger ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span /><span /><span />
        </button>
        <ul className={`navbar-links ${menuOpen ? 'navbar-links--open' : ''}`}>
          {navLinks.map((link) => {
            // PR-6: the screens-dropdown is the only non-Link item.
            // It renders as a controlled button that toggles a small
            // panel of the 4 specialty-screen Links.
            if (link.type === 'screens-dropdown') {
              return (
                <li
                  key="screens-dropdown"
                  className="navbar-screens-dropdown"
                  ref={screensRef}
                  data-testid="navbar-screens-dropdown"
                >
                  <button
                    type="button"
                    className={`navbar-link navbar-screens-toggle ${isOnScreens ? 'active' : ''}`}
                    onClick={() => setScreensOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={screensOpen}
                    data-testid="navbar-screens-toggle"
                  >
                    Screens
                    <span className="navbar-screens-chevron" aria-hidden="true">
                      {screensOpen ? '▾' : '▸'}
                    </span>
                  </button>
                  {screensOpen && (
                    <ul
                      className="navbar-screens-menu"
                      role="menu"
                      data-testid="navbar-screens-menu"
                    >
                      {screenLinks.map((s) => {
                        const sActive = location.pathname === s.path;
                        return (
                          <li key={s.path} role="none">
                            <Link
                              to={s.path}
                              role="menuitem"
                              className={`navbar-screens-menu-item ${sActive ? 'active' : ''}`}
                              data-testid={`navbar-screens-item-${s.path.split('/').pop()}`}
                            >
                              {s.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            }
            const isActive = location.pathname === link.path;
            return (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`navbar-link ${isActive ? 'active' : ''}`}
                >
                  {link.label}
                  {link.badge > 0 && <span className="navbar-badge">{link.badge}</span>}
                </Link>
              </li>
            );
          })}
          {user ? (
            <li className="navbar-user" data-testid="navbar-user">
              <span className="navbar-user-email" title={user.email}>{user.email}</span>
              <button
                type="button"
                className="navbar-logout-btn"
                onClick={handleLogout}
              >
                Log out
              </button>
            </li>
          ) : (
            <li>
              <Link to="/login" className="navbar-signin-link" data-testid="navbar-signin">
                Sign in
              </Link>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default Navbar;
